import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Notification } from './notification.interface';
import { ProgressWheelComponent } from '../progress-wheel/progress-wheel.component';

/**
 * Individual notification item component
 * Displays a single notification with icon, message, progress indicator, and close button
 */
@Component({
	selector: 'app-notification-item',
	standalone: true,
	imports: [CommonModule, ProgressWheelComponent],
	templateUrl: './notification-item.component.html',
	styleUrls: ['./notification-item.component.scss'],
})
export class NotificationItemComponent implements OnInit, OnDestroy, OnChanges {
	@Input() notification!: Notification;
	@Output() closeNotification = new EventEmitter<string>();

	private defaultMinimizeTimeout = 5000;

	private timeoutId: number | null = null;
	// Track remaining timeout time when paused (in milliseconds)
	private remainingTime = 0;
	// Track when timeout started (timestamp in milliseconds)
	private timeoutStartTime = 0;
	// Track if timeout is currently paused
	private isPaused = false;

	// Auto-minimize feature for spinner notifications
	isMinimized = false;
	private minimizeTimeoutId: number | null = null;
	private previousMessage = '';
	private previousProgress = -1;
	private previousTimeout = -1;
	private wasHovered = false;

	ngOnInit(): void {
		// Initialize previous values for change detection
		this.previousMessage = this.notification.message;
		this.previousProgress = this.notification.progress;
		this.previousTimeout = this.notification.timeout;

		// Start timeout if configured
		if (this.notification.timeout > 0) {
			this.startTimeout(this.notification.timeout);
		}

		// Start minimize timer for notifications without timeout
		if (this.notification.timeout === -1) {
			this.startMinimizeTimeout();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Check if message or progress changed (content update)
		if (changes['notification']) {
			const currentNotification = changes['notification']
				.currentValue as Notification;
			const isContentUpdate =
				currentNotification.message !== this.previousMessage ||
				currentNotification.progress !== this.previousProgress;

			// Only expand if message changed (not just progress)
			const shouldExpand =
				currentNotification.message !== this.previousMessage;

			if (isContentUpdate && !changes['notification'].firstChange) {
				// Only expand notification when message changes (not just progress)
				if (shouldExpand) {
					this.isMinimized = false;
				}

				// Restart minimize timer for notifications without timeout
				if (this.notification.timeout === -1) {
					this.startMinimizeTimeout();
				}
			}

			// Check if timeout changed from no-timeout to auto-close (e.g., via autoClose: true)
			if (
				this.previousTimeout <= 0 &&
				currentNotification.timeout > 0 &&
				!changes['notification'].firstChange
			) {
				// Start auto-close timer with the new timeout value
				this.startTimeout(currentNotification.timeout);
			}

			// Update previous values
			this.previousMessage = currentNotification.message;
			this.previousProgress = currentNotification.progress;
			this.previousTimeout = currentNotification.timeout;
		}
	}

	ngOnDestroy(): void {
		// Clear timeout when component is destroyed
		if (this.timeoutId !== null) {
			window.clearTimeout(this.timeoutId);
		}
		// Clear minimize timeout when component is destroyed
		if (this.minimizeTimeoutId !== null) {
			window.clearTimeout(this.minimizeTimeoutId);
		}
	}

	/**
	 * Start or restart timeout with specified duration
	 * @param duration - Duration in milliseconds
	 */
	private startTimeout(duration: number): void {
		this.remainingTime = duration;
		this.timeoutStartTime = Date.now();
		this.timeoutId = window.setTimeout(() => {
			this.onClose();
		}, duration);
	}

	/**
	 * Start minimize timer for spinner notifications (4 seconds)
	 */
	private startMinimizeTimeout(): void {
		// Clear existing minimize timeout
		if (this.minimizeTimeoutId !== null) {
			window.clearTimeout(this.minimizeTimeoutId);
		}

		// Set new minimize timeout (4 seconds)
		this.minimizeTimeoutId = window.setTimeout(() => {
			// Only minimize if not currently hovered
			if (!this.wasHovered) {
				this.isMinimized = true;
			}
			this.minimizeTimeoutId = null;
		}, this.defaultMinimizeTimeout);
	}

	/**
	 * Pause the timeout when user hovers over notification
	 */
	onMouseEnter(): void {
		this.wasHovered = true;

		// Expand if minimized
		if (this.isMinimized) {
			this.isMinimized = false;
		}

		if (this.timeoutId !== null && !this.isPaused) {
			// Calculate remaining time
			const elapsedTime = Date.now() - this.timeoutStartTime;
			this.remainingTime = this.remainingTime - elapsedTime;

			// Clear current timeout
			window.clearTimeout(this.timeoutId);
			this.timeoutId = null;
			this.isPaused = true;
		}
	}

	/**
	 * Resume the timeout when user stops hovering
	 */
	onMouseLeave(): void {
		// Restore minimized state for notifications without timeout
		if (this.notification.timeout === -1) {
			this.isMinimized = true;
		}

		if (this.isPaused && this.remainingTime > 0) {
			this.isPaused = false;
			this.startTimeout(this.remainingTime);
		}
	}

	/**
	 * Handle close button click
	 * Emit close event - container animation will handle the exit
	 */
	onClose(): void {
		this.closeNotification.emit(this.notification.id);
	}

	/**
	 * Handle action button click
	 * Execute the callback function if action button is configured
	 */
	onActionButtonClick(): void {
		if (this.notification.actionButton) {
			this.notification.actionButton.callback();
		}
	}

	/**
	 * Check if notification has a progress indicator (spinner or progress wheel)
	 */
	get hasProgressIndicator(): boolean {
		return (
			this.notification.progress === -2 ||
			(this.notification.progress >= 0 &&
				this.notification.progress <= 100)
		);
	}

	/**
	 * Get truncated message for minimized state
	 * Uses 20 characters, but if there's a continuous word longer than 10 characters, limit to 10
	 */
	get truncatedMessage(): string {
		const message = this.notification.message;
		const maxLength = 20;
		const wordBreakLimit = 10;

		// If message is shorter than max length, return as is
		if (message.length <= maxLength) {
			return message;
		}

		// Get substring up to max length
		const truncated = message.substring(0, maxLength);

		// Check if there's a word longer than wordBreakLimit in the truncated text
		const words = truncated.split(/\s+/);
		const hasLongWord = words.some((word) => word.length > wordBreakLimit);

		if (hasLongWord) {
			// If there's a long word, limit to wordBreakLimit characters
			return message.substring(0, wordBreakLimit) + '...';
		}

		// Otherwise use the full maxLength
		return truncated + '...';
	}
}
